import { getReviewImages, getReviewList, getCustomerReview } from '@ugc-api/review';
import { getThumbStatus } from '@ugc-api/helpful-status';
import state from '@ugc-store';
import { toProfileImageUrl, stringFormat, getProductIdFromRoute } from '@ugc-utils/utils';
import { defaultReviewPending, ICSOrVendorComments, TopReviewType } from './interface';

export const imagePaginationLimit = state.isDesktop ? 15 : 21;

export const toReviewShareUrl = (id, customerProfileLink) => {
  if (!id) return '';
  const productId = getProductIdFromRoute();
  return `https://${window.location.host}/ugc/sharedreview/${productId}/${id}/${
    customerProfileLink || undefined
  }`.toLowerCase();
};

export const toTranslatedText = ({ translatedTo, languageName }) => {
  const {
    reviewStaticLanguage: { IDS_UGC_AUTO_TRANSLATED_FROM = '' },
  } = state;
  let translatedText = '';
  if (translatedTo) {
    translatedText = stringFormat(IDS_UGC_AUTO_TRANSLATED_FROM, languageName || '');
  }
  return translatedText;
};

const toBadge = badge => {
  return badge && badge.translation
    ? {
        type: badge.name,
        name: badge.translation.name,
        title: badge.translation.title,
        description: badge.translation.description,
      }
    : null;
};
export const toImages = ({ images, reviewId }) => {
  if (!images.length) return [];
  return images.map(({ assetId, paths }) => ({
    assetId,
    paths,
    reviewId,
  }));
};

const toCsOrVendorComments = (comments?: ICSOrVendorComments) => {
  if (comments) {
    const { content, postedDateLocalized } = comments;
    return {
      content,
      postedDateLocalized,
    };
  }
  return null;
};

const reviewFormat = review => {
  const {
    id,
    customerNickname,
    customerProfileLink,
    profileInfo: {
      ugcSummary: { answerCount = 0, helpfulCount = 0, imageCount = 0, reviewCount = 0 } = {},
      badge = null,
      country = '',
    } = {},
    ratingValue = 0,
    postedDateLocalized,
    reviewTitle,
    reviewText,
    verifiedPurchase,
    hasRewarded,
    images = [],
    helpfulYes,
    helpfulNo,
    profileInfo = {},
    hasThumbsUp = null,
    vendorComments,
    customerServiceComments,
    languageCode = '',
    languageName = '',
    translatedTo = '',
    translatedToLanguageName = '',
  } = review;
  return {
    id,
    customerNickname, // reviewer name
    customerProfileLink, // reviewer link
    profileImageUrl: toProfileImageUrl(profileInfo), // user avatar
    summary: { reviewCount, imageCount, answerCount, helpfulCount }, // four icon number
    badge: toBadge(badge),
    ratingValue: ratingValue / 10, // rating value
    reviewTitle, // review title
    reviewText, // review text
    images: toImages({ images, reviewId: id }),
    shareUrl: toReviewShareUrl(id, customerProfileLink), // title share url
    postedDateLocalized, // post date
    verifiedPurchase, // verified purchase
    hasRewarded, // rewarded
    helpfulYes, // helpful yes
    helpfulNo, // helpful no
    hasThumbsUp, // helpful
    vendorComments: toCsOrVendorComments(vendorComments),
    customerServiceComments: toCsOrVendorComments(customerServiceComments),
    languageCode,
    translatedTo,
    translatedToLanguageName,
    languageName,
    country,
  };
};

export const toReviewInfoList = reviewList => {
  let ids = [];
  let reviewBreakUpById = {};

  if (reviewList.length) {
    reviewList.forEach(review => {
      ids.push(review.id);
      reviewBreakUpById[review.id] = reviewFormat(review);
    });
  }
  return {
    ids,
    reviewBreakUpById,
  };
};

const toAverageRating = averageRating => {
  if (isNaN(averageRating)) return '0.0';
  if (typeof averageRating === 'string') return averageRating;
  return averageRating.toFixed(1);
};

const toSummaryReview = item => {
  const {
    id,
    customerNickname,
    customerProfileLink,
    profileInfo: {
      ugcSummary: { answerCount = 0, helpfulCount = 0, imageCount = 0, reviewCount = 0 } = {},
      badge = null,
      country = '',
    } = {},
    ratingValue = 0,
    postedDateLocalized,
    reviewTitle,
    reviewText,
    verifiedPurchase,
    hasRewarded,
    images = [],
    helpfulYes,
    helpfulNo,
    profileInfo = {},
    vendorComments,
    customerServiceComments,
    languageCode = '',
    languageName = '',
    translatedTo = '',
    translatedToLanguageName = '',
  } = item;
  return {
    id,
    customerNickname, // reviewer name
    customerProfileLink, // reviewer link
    profileImageUrl: toProfileImageUrl(profileInfo), // user avatar
    summary: { reviewCount, imageCount, answerCount, helpfulCount }, // four icon number
    badge: toBadge(badge),
    ratingValue: ratingValue / 10, // rating value
    reviewTitle, // review title
    reviewText, // review text
    images: toImages({ images, reviewId: id }),
    shareUrl: toReviewShareUrl(id, customerProfileLink), // title share url
    postedDateLocalized, // post date
    verifiedPurchase, // verified purchase
    hasRewarded, // rewarded
    helpfulYes, // helpful yes
    helpfulNo, // helpful no
    hasThumbsUp: null, // helpful
    vendorComments: toCsOrVendorComments(vendorComments),
    customerServiceComments: toCsOrVendorComments(customerServiceComments),
    languageCode,
    languageName,
    translatedTo,
    country,
    translatedToLanguageName,
  };
};
export const toSummary = summary => {
  const {
    preference: { language: lc },
  } = state;
  const { rating = {}, languages = [] } = summary || {};
  const { fiveStar, fourStar, threeStar, twoStar, oneStar } = rating;
  const filterLanguage = languages.find(language => language.languageCode === lc);
  const { topCriticalReview = null, topPositiveReview = null } = filterLanguage || {};
  return {
    averageRating: toAverageRating(rating.averageRating),
    calculatedRating: isNaN(rating.calculatedRating) ? 0 : rating.calculatedRating,
    count: isNaN(rating.count) ? 0 : rating.count,
    processItems: [
      { ...fiveStar, value: 5 },
      { ...fourStar, value: 4 },
      { ...threeStar, value: 3 },
      { ...twoStar, value: 2 },
      { ...oneStar, value: 1 },
    ],
    [TopReviewType.TopPositiveReview]: topPositiveReview ? toSummaryReview(topPositiveReview) : null,
    [TopReviewType.TopCriticalReview]: topCriticalReview ? toSummaryReview(topCriticalReview) : null,
    languages,
  };
};

export const toUserReviewData = userReviewData => {
  const {
    id,
    status,
    isFrozen,
    ratingValue,
    reviewText,
    reviewTitle,
    images,
    verifiedPurchase,
    hasRewarded,
    postedDateLocalized,
  } = userReviewData;
  return {
    id,
    status,
    isFrozen,
    ratingValue: ratingValue / 10,
    reviewTitle,
    reviewText,
    images: toImages({ images, reviewId: id }),
    verifiedPurchase,
    hasRewarded,
    postedDateLocalized,
  };
};

export const toSingleReviewInfo = item => {
  const {
    id,
    customerNickname,
    customerProfileLink,
    profileInfo: {
      ugcSummary: { answerCount = 0, helpfulCount = 0, imageCount = 0, reviewCount = 0 } = {},
      badge = null,
      country = '',
    } = {},
    ratingValue = 0,
    postedDateLocalized,
    reviewTitle: originReviewTitle,
    reviewText: originReviewText,
    verifiedPurchase,
    hasRewarded,
    images = [],
    helpfulYes,
    helpfulNo,
    profileInfo = {},
    hasThumbsUp = null,
    vendorComments,
    customerServiceComments,
    languageCode,
    languageName,
    translations,
  } = item;
  const {
    preference: { language },
  } = state;
  let reviewText = '';
  let reviewTitle = '';
  let translatedTo = '';
  let translatedToLanguageName = '';
  if (language === languageCode) {
    reviewText = originReviewText;
    reviewTitle = originReviewTitle;
  } else {
    const translationReview =
      translations.find(({ languageCode: translationLanguageCode }) => translationLanguageCode === language) ||
      translations.find(({ languageCode: translationLanguageCode }) => translationLanguageCode === 'en-US');
    reviewText = translationReview ? translationReview.reviewText : originReviewText;
    reviewTitle = translationReview ? translationReview.reviewTitle : originReviewTitle;
    translatedTo = translationReview ? translationReview.languageCode : '';
    translatedToLanguageName = translationReview ? translationReview.languageName : '';
  }
  return {
    id,
    customerNickname, // reviewer name
    customerProfileLink, // reviewer link
    profileImageUrl: toProfileImageUrl(profileInfo), // user avatar
    summary: { reviewCount, imageCount, answerCount, helpfulCount }, // four icon number
    badge: toBadge(badge),
    ratingValue: ratingValue / 10, // rating value
    reviewTitle, // review title
    reviewText, // review text
    images: toImages({ images, reviewId: id }),
    shareUrl: toReviewShareUrl(id, customerProfileLink), // title share url
    postedDateLocalized, // post date
    verifiedPurchase, // verified purchase
    hasRewarded, // rewarded
    helpfulYes, // helpful yes
    helpfulNo, // helpful no
    hasThumbsUp, // helpful
    vendorComments: toCsOrVendorComments(vendorComments),
    customerServiceComments: toCsOrVendorComments(customerServiceComments),
    languageCode,
    translatedTo,
    translatedToLanguageName,
    languageName,
    country,
  };
};

export const getReviewListData = async ({ productId, language, isFirstRender = false }) => {
  try {
    const params = {
      pid: productId,
      page: 1,
      sortId: 6,
      lc: language,
      textToSearch: '',
      limit: state.isDesktop ? 5 : 3,
      withUgcSummary: true,
      withImagesOnly: false,
      isShowTranslated: true,
      withoutDefaultTitle: true,
    };
    state.isReviewLoading = true;
    state.isReviewListLoaded = false;
    state.isShowReviewSkeleton = isFirstRender;
    const { items = [], totalCount = 0 } = (await getReviewList(params)) || {};
    const { ids, reviewBreakUpById } = toReviewInfoList(items);

    state.ids = ids;
    state.reviewBreakUpById = reviewBreakUpById;

    state.reviewTotalCount = totalCount;
    state.isReviewLoading = false;
    state.isReviewListLoaded = true;
    state.isShowReviewSkeleton = false;
  } catch (e) {
    state.isReviewLoading = false;
    state.isReviewListLoaded = false;
    state.isShowReviewSkeleton = false;
  }
};

export const getCustomerReviewData = async productId => {
  try {
    const userReviewData = (await getCustomerReview(productId)) || {};

    if (JSON.stringify(userReviewData) !== '{}') {
      state.isUpdateReview = true;
    } else {
      state.isUpdateReview = false;
    }

    state.userReviewData = toUserReviewData(userReviewData);
  } catch (e) {
    state.userReviewData = defaultReviewPending;
    state.isUpdateReview = false;
    switch (e.status) {
      case 401:
      case 403:
        state.isShowPendingReview = false;
        break;
    }
  }
};

export const getReviewImagesData = async productId => {
  try {
    const data = await getReviewImages(productId);
    data.items = Array.isArray(data.items) ? data.items : [];
    data.totalCount = data.totalCount > 0 ? data.totalCount : 0;
    state.reviewImageListData = data;
    state.imagePagination = {
      current: 1,
      pages: Math.ceil(data.totalCount / imagePaginationLimit),
      nextPageToken: Number(data.nextPageToken),
    };
  } catch (e) {}
};

export const getReviewMoreImagesData = async () => {
  try {
    const {
      productInfo: { productId },
      imagePagination: { nextPageToken },
    } = state;
    if (!nextPageToken) return;
    const data = await getReviewImages(productId, nextPageToken);
    const items = Array.isArray(data.items) ? data.items : [];
    state.reviewImageListData = {
      items: [...state.reviewImageListData.items, ...items],
      totalCount: data.totalCount,
    };
    state.imagePagination = {
      current: Number(nextPageToken),
      pages: Math.ceil(data.totalCount / imagePaginationLimit),
      nextPageToken: Number(data.nextPageToken),
    };
  } catch (e) {}
};

export const getReviewImagesDataByPageNumber = async pageNumber => {
  try {
    const {
      productInfo: { productId },
    } = state;
    const data = await getReviewImages(productId, pageNumber);
    return Array.isArray(data.items) ? data.items : [];
  } catch (e) {
    return [];
  }
};

export const getThumbStatusData = async () => {
  const { ids } = state;
  if (ids.length) {
    try {
      const data = await getThumbStatus(ids);
      const reviewBreakUpById = state.reviewBreakUpById;
      if (data && data.length) {
        data.forEach(({ id, vote }) => (reviewBreakUpById[id].hasThumbsUp = vote));
      }
      state.reviewBreakUpById = reviewBreakUpById;
    } catch (error) {}
  }
};

export const getSummaryThumbStatusData = async () => {
  const { topCriticalReview, topPositiveReview } = state.reviewSummary;
  const topReviewIds = [
    topCriticalReview ? topCriticalReview.id : null,
    topPositiveReview ? topPositiveReview.id : null,
  ].filter(Boolean) as string[];
  if (topReviewIds.length) {
    try {
      const data = await getThumbStatus(topReviewIds);
      if (data && data.length) {
        data.forEach(({ id, vote }) => {
          Object.values(TopReviewType).forEach(type => {
            if (state.reviewSummary[type].id && state.reviewSummary[type].id === id) {
              state.reviewSummary = {
                ...state.reviewSummary,
                [type]: {
                  ...state.reviewSummary[type],
                  hasThumbsUp: vote,
                },
              };
            }
          });
        });
      }
    } catch (error) {}
  }
};
